<template>
  <!-- eslint-disable max-len -->
  <div>
    <form
      ref="form"
      @submit.prevent="validate(); analytics.form.submitted();"
    >
      <section class="py-4">
        <Tooltip
          class="block"
          @show="analytics.click.tooltipEmail();"
        >
          <InputText
            v-model.trim="email"
            class="mt-3"
            label="Email Address"
            placeholder="Email Address"
            name="email"
            :has-error="errors.email.hasError"
            :disabled="!primaryBusiness"
            @blur="analytics.input.email();"
            @input="errors.email.hasError = false"
          >
            <span
              v-if="errors.email.hasError"
              slot="helper"
            >
              {{ errors.email.message }}
            </span>
          </InputText>
          <template slot="tip">
            <p>
              Only the email address of the "Primary" business listed in the saved businesses section can be edited. Changes to the email address will be applied to all other business names and addresses.
            </p>
          </template>
          <template slot="accessible">
            Email address use
          </template>
        </Tooltip>
        <div class="mt-3">
          <p class="text-sm">
            By checking the box below, you are opting-in to a one-time reminder email in the event you don't complete your Change-of-Address (COA) request. If checked, USPS will send one email with a link to resume your COA. Your COA won't take effect until the full form is complete.
          </p>
        </div>
        <div class="mt-3">
          <label class="reminder_email--label">
            <span>
              I opt-in, to receive a one-time reminder email
            </span>
            <input
              id="optin-mail-reminder"
              :checked="optInMailReminder"
              class="mt-3"
              type="checkbox"
              @click="optInMailReminder = !optInMailReminder; analytics.click.optInReminder(optInMailReminder)"
            >
            <span class="checkmark" />
          </label>
        </div>
        <InputPhone
          v-model="phoneNumber"
          class="mt-4"
          label="Phone Number"
          placeholder="(000) 000-0000"
          name="phone"
          :has-error="errors.phoneNumber.hasError"
          :disabled="!primaryBusiness"
          @blur="analytics.input.phone();"
          @input="errors.phoneNumber.hasError = false"
        >
          <span
            v-if="errors.phoneNumber.hasError"
            slot="helper"
          >
            {{ errors.phoneNumber.message }}
          </span>
        </InputPhone>
        <Tooltip
          class="block"
        >
          <InputDropdown
            v-model="phoneType"
            class="mt-3"
            label="Phone Type"
            name="phone-type"
            :has-error="errors.phoneType.hasError"
            :disabled="!primaryBusiness"
            @input="analytics.input.phoneType();"
          >
            <option value="">
              Select
            </option>
            <option value="HOME">
              Home
            </option>
            <option value="MOBILE">
              Mobile
            </option>
            <span
              v-if="errors.phoneType.hasError"
              slot="helper"
            >
              {{ errors.phoneType.message }}
            </span>
          </InputDropdown>
          <template slot="tip">
            <p>
              Only the phone and phone type of the "Primary" business listed in the saved businesses section can be edited. Changes to the phone or phone type will be applied to all other business names and addresses.
            </p>
          </template>
          <template slot="accessible">
            Phone number use
          </template>
        </Tooltip>
        <Tooltip
          class="block"
          @show="analytics.click.tooltipForwardType();"
        >
          <InputDropdown
            v-model="forwardType"
            class="mt-3"
            label="Type of Move"
            name="forward-type"
            :has-error="errors.forwardType.hasError"
            @input="$event && analytics.click.forwardType($event);"
          >
            <option value="">
              Select
            </option>
            <option value="PERMANENT">
              Permanent
            </option>
            <option value="TEMPORARY">
              Temporary
            </option>
            <span
              v-if="errors.forwardType.hasError"
              slot="helper"
            >
              {{ errors.forwardType.message }}
            </span>
          </InputDropdown>
          <template slot="tip">
            <p>
              <span class="font-bold">Permanent:</span> Choose this option if you have no plans to move back to your old address. (Most moves are permanent.)
            </p>
            <p class="mt-2">
              <span class="font-bold">Temporary:</span> Choose this option if you plan to move back to your old address in less than six months. You can always extend your temporary forwarding for up to 12 months.
            </p>
            <p class="mt-2">
              If you plan to move to a new address for less than 6 months and then to a different address (not your old address), choose "Permanent" and fill out another Change-of-Address request at that time.
            </p>
          </template>
          <template slot="accessible">
            Permanent versus temporary description
          </template>
        </Tooltip>
        <Tooltip
          class="block"
          @show="analytics.click.tooltipForwardStartDate();"
        >
          <InputDatePicker
            v-model="startDate"
            :label="forwardType !== 'TEMPORARY' ? 'Mail Forwarding Date' : 'Start Forwarding'"
            :min="minStartDate"
            :max="maxStartDate"
            name="forward-start-date"
            placeholer="mm/dd/yyyy"
            class="mt-3"
            :has-error="errors.startDate.hasError"
            @input="analytics.input.startDate();"
          >
            <span slot="helper">
              {{ errors.startDate.message }}
            </span>
          </InputDatePicker>
          <template slot="tip">
            <p>
              Enter the date you want us to begin forwarding mail to your new address. The date must not be more than 30 days in the past or more than 3 months in the future. Please allow at least 7-10 business days to begin receiving mail at your new address.
            </p>
          </template>
          <template slot="accessible">
            Forwarding dates description
          </template>
        </Tooltip>
        <InputDatePicker
          v-if="forwardType === 'TEMPORARY'"
          v-model="stopDate"
          :min="minStopDate"
          :max="maxStopDate"
          :open-date="minStopDate"
          label="End Forwarding"
          name="forward-stop-date"
          placeholer="mm/dd/yyyy"
          class="mt-3"
          :has-error="errors.stopDate.hasError"
          @input="analytics.input.stopDate();"
        >
          <template #rules>
            <p class="mb-4">
              The minimum length for filing a temporary Change-of-Address is 15 days.
            </p>
            <p>
              The initial forwarding period is restricted to 6 months, but can be extended up to 12 months.
            </p>
          </template>
          <span slot="helper">
            {{ errors.stopDate.message }}
          </span>
        </InputDatePicker>
      </section>
      <div class="c-hairline mt-4" />
      <MultipleBusinessAddresses
        ref="multipleAddressesComponent"
        :forward-type-val="forwardType"
        :is-simplify="false"
        :start-date-val="startDate"
        :stop-date-val="stopDate"
        :business-whole-company-val="businessWholeCompany"
        :email-val="email"
        :phone-number-val="phoneNumber"
        :phone-type-val="phoneType"
        :opt-in-mail-reminder-val="optInMailReminder"
        view-mode="verifyEdit"
        this.errors
        @primary="setPrimaryBusiness"
        @validateForm="validateForm()"
        @fieldErrors="(fieldErrors)=>updateFieldErrors(fieldErrors)"
        @fieldErrorsMilitary="updateFieldErrorsMilitary"
        @disableNonBusinessMovers="disableMoverType"
      />
    </form>
    <div class="mt-6 text-sm">
      <p>
        <span class="font-bold">
          Why your name and address might look different than how you entered them:
        </span>
        Special characters in your name (like apostrophes) may have been removed and your address(es) may have been standardized to match the official U.S. Postal Service format. This helps forward your mail more effectively.
      </p>
    </div>
    <div class="c-hairline mt-6" />
    <section class="mt-6">
      <p class="font-bold text-base">
        Request for Change-of-Address and Forwarding Services
      </p>
      <p class="mt-3 mb-6 text-base">
        <strong>Privacy Act Statement:</strong>  Your information will be used to provide mail forwarding
        and Change-of-Address (COA) services and move related advertisements, to verify your identity,
        to facilitate communication, to facilitate surveys regarding the COA process, to identify and mitigate potential fraud,
        and to provide address correction services. Collection is authorized by 39 U.S.C. 401, 403, 404.
        Providing the information is voluntary,
        but if not supplied, we may not be able to process and fulfill your request for services.
        We do not disclose your information to third parties without your
        consent, except to facilitate the transaction, to act on your behalf
        or request, or as legally required.
        <a
          v-if="!privacyActFullContent"
          class="font-bold mt-1"
          :class="['c-link--choice', 'c-link--inline']"
          type="button"
          @click="togglePrivacyActContent"
        >
          Click here to display the entire Privacy Act Statement
        </a>
        <template v-if="privacyActFullContent">
          This includes the following standard
          and specific routine uses: Incident to legal proceedings; for law enforcement
          purposes; to a congressional office; to agents or contractors; to auditors;
          to labor organizations; to federal, state, local, or foreign government
          agencies; to agencies and entities for financial matters; for customer service
          purposes; to a federal, state, or local government agency upon certification
          when required for the performance of its duties; to a law enforcement agency
          after the Postal Inspection Service has confirmed that the COA information is
          needed for a criminal investigation; for service of process; for purpose of
          jury service; to agencies or entities at customer’s request; to a disaster
          relief organization; pursuant to the order of a court of competent jurisdiction;
          for address correction services only if the mailer is in possession of the name
          and old address; and to USPS licensed service providers to perform mailing list
          address correction services for lists containing your name and old address. For
          a list of licensed service providers please visit <a
            href="https://postalpro.usps.com/NCOALink_Service_Providers"
            :class="['c-link--choice', 'c-link--inline']"
          >https://postalpro.usps.com/NCOALink_Service_Providers</a>. For more information
          regarding our privacy policies visit <a
            href="https://www.usps.com/privacypolicy"
            target="_blank"
            :class="['c-link--choice', 'c-link--inline']"
            rel="noopener noreferrer"
          >www.usps.com/privacypolicy</a>.
          <br>
          <a
            :class="['c-link--choice', 'c-link--inline']"
            class="mt-1"
            type="button"
            @click="togglePrivacyActContent"
          >
            Display Less
          </a>
        </template>
      </p>
    </section>
    <Modal
      id="new-constr-close-x"
      v-model="newConstructionModal"
    >
      <ModalNewConstruction
        :street-address="newStreetAddress"
        :city="newCity"
        :state="newState"
        :zip-code="newZipCode"
        :urbanization-name="newUrbanizationName"
        @reset="newConstructionModal = false"
        @override="newConstructionOverride"
      />
    </Modal>
    <Modal
      ref="emailReminderOptOutModal"
      v-model="emailReminderOptOutModal"
    >
      <h4>One-Time Email Opt-Out</h4>
      <p class="mt-3">
        You've opted-out of the one-time reminder email if you don't complete this Change-of-Address transaction.
      </p>
      <p class="mt-3">
        You will still receive confirmation emails if you complete your transaction.
      </p>
      <button
        class="c-btn mt-3"
        @click="emailReminderOptOutModal=false"
      >
        Close
      </button>
    </Modal>
    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address you've entered. To make changes to your current Change-of-Address, visit
        <a
          class="c-link c-link--inline"
          :href="this.$sanitize($options.coacUrl)"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code, which can be located within your confirmation email and the Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </div>
</template>

<script>
/* eslint-disable default-case */
import {
  InputDatePicker,
  InputDropdown,
  InputPhone,
  InputText,
  Modal,
  Tooltip,
} from '@mgo/ui/lib/components';
import { mobileAnalytics } from '../VerifyInfoEdit/analytics';
import ModalNewConstruction from '../../components/ModalNewConstruction.vue';

// the mixins and shared logic
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import emailReminder from '../../mixins/emailReminder';
import moverType from '../../mixins/moverType';
import newConstruction from '../../mixins/newConstruction';
import rapidKey from '../../mixins/rapidKey';
import destinationManager from '../../mixins/destinationManager';
import verifyInfoEditMixin from './mixin';
import MultipleBusinessAddresses from '../../components/MultipleAddresses/mobile.vue';

export default {
  name: 'MobileVerifyInfoEditMB',
  components: {
    InputDatePicker,
    InputDropdown,
    InputPhone,
    InputText,
    Modal,
    ModalNewConstruction,
    MultipleBusinessAddresses,
    Tooltip,
  },
  mixins: [
    addressViolations,
    datePicker,
    emailReminder,
    moverType,
    newConstruction,
    rapidKey,
    verifyInfoEditMixin,
    destinationManager,
  ],
  data() {
    return {
      privacyActFullContent: false,
    };
  },
  computed: {
    analytics() {
      return mobileAnalytics();
    },
  },
  methods: {
    togglePrivacyActContent() {
      this.privacyActFullContent = !this.privacyActFullContent;
    },
  },
};
</script>
<style>
@import '../../css/views/verify-info.css';
</style>
